import { useState } from 'react';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';
import { Card } from '@/components/Card';

// Self-service settings panel (webapp): a logged-in user mints a fresh, long-
// lived sign-in link to open on another device (phone, laptop). The link is
// reusable until it expires, so the same URL works across machines. There's no
// email delivery — the user copies it and opens it wherever they need. Anyone
// holding the link can sign in as this user, hence the warning.
interface PortableLinkResult {
  url: string;
  expires_at: number;
}

export function PortableLinkPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PortableLinkResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function generate() {
    if (busy) return;
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const r = await fetch('/api/auth/portable-link', {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        const text = await r.text();
        throw new Error(text || `request failed (${r.status})`);
      }
      setResult((await r.json()) as PortableLinkResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked — the link is selectable in the field as a fallback.
    }
  }

  return (
    <Card className="space-y-3">
      <h2 className="text-lg font-semibold">Sign in on another device</h2>
      <p className="text-xs text-slate-600">
        Generate a sign-in link to open on your phone or another computer. It stays valid for
        30 days and can be reused on as many devices as you like — bookmark it to get back in
        without waiting for a new link.
      </p>
      <p className="text-xs text-amber-700">
        Treat it like a password: anyone with the link can sign in as you.
      </p>

      <Button variant="primary" onClick={() => void generate()} loading={busy}>
        {busy ? 'Generating…' : result ? 'Generate a new link' : 'Generate sign-in link'}
      </Button>

      {result && (
        <div className="space-y-2 rounded border border-green-200 bg-green-50 p-3 text-xs">
          <div className="text-green-800">
            Link expires {new Date(result.expires_at).toLocaleString()}.
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={result.url}
              onFocus={(e) => e.currentTarget.select()}
              className="min-w-0 flex-1 rounded border border-slate-300 bg-card px-2 py-1 font-mono text-[11px]"
            />
            <Button size="xs" className="shrink-0" onClick={() => void copyLink()}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      )}
      {error && <Alert>{error}</Alert>}
    </Card>
  );
}
